import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, CalendarDays, Play, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { dailyGame } from "@/lib/daily";
import { useAuth } from "@/context/AuthContext";

export function DailyChallenge() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const game = dailyGame();
  const streak = user?.daily_streak || 0;
  const completedToday = user && user.daily_last === new Date().toISOString().slice(0, 10);

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 -mt-px">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="relative rounded-2xl border-2 border-neon-lime bg-zinc-950 overflow-hidden grid md:grid-cols-[1.2fr_1fr] gap-0"
        data-testid="daily-challenge">
        <div className="p-7 md:p-10">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase tracking-widest bg-neon-lime/10 border border-neon-lime text-neon-lime rounded-full">
            <CalendarDays className="w-3 h-3" /> Daily Challenge
          </span>
          <h2 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter mt-4">
            Today: <span className="text-neon-lime">{game.title}</span>
          </h2>
          <p className="text-zinc-400 mt-2 max-w-md">{game.blurb} Play it every day to build your streak!</p>

          <div className="flex items-center gap-5 mt-6">
            <div className="flex items-center gap-2" data-testid="daily-streak">
              <Flame className={`w-7 h-7 ${streak > 0 ? "text-orange-400" : "text-zinc-600"}`} />
              <div>
                <p className="font-display font-black text-2xl leading-none">{streak}</p>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Day streak</p>
              </div>
            </div>
            {user?.daily_best > 0 && (
              <div className="flex items-center gap-2">
                <Trophy className="w-6 h-6 text-neon-yellow" />
                <div>
                  <p className="font-display font-black text-2xl leading-none">{user.daily_best}</p>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Best</p>
                </div>
              </div>
            )}
          </div>

          <Button onClick={() => navigate(`/play/${game.id}`)} data-testid="daily-play-btn"
            className="mt-6 bg-neon-lime text-black rounded-none font-bold uppercase tracking-widest h-12 px-8 hover:bg-white">
            <Play className="w-4 h-4 mr-2 fill-black" /> {completedToday ? "Play Again" : "Play Today's Challenge"}
          </Button>
          {completedToday && <p className="text-neon-lime text-sm mt-3 font-mono">✓ You've completed today's challenge!</p>}
          {!user && <p className="text-zinc-500 text-sm mt-3">Log in to track your streak.</p>}
        </div>

        <div className="relative min-h-[200px] hidden md:block">
          <img src={game.image} alt={game.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/40 to-transparent" />
        </div>
      </motion.div>
    </section>
  );
}
